import {useNavigate} from 'react-router-dom';
import * as css from './menu.css';
import {useSelector} from 'react-redux';
import {useEffect, useMemo} from 'react';
import useMobile from '../../../hooks/useMobile';
import useShow from '../../../hooks/useShow';
import List from '../../../component/list/List';
import {useTranslation} from 'react-i18next';
import {RootState} from '../../../redux/store.type';
import MessageTypeItem from './components/MessageTypeItem';
import DeliveryTypeMenu from './components/DeliveryTypeMenu';
import {messageTypeConfig, deliveryTypeIcons, DELIVERY_TYPES, MESSAGE_TYPES} from './brandingConfig';
import {useBrandingParams} from '../../../hooks/useBrandingParams';
import {eventPathHelper} from './brandingHelper';
import {getAllowedDeliveryTypes, resolveDeliveryType} from './brandingRouteHelper';

const PERMISSIONS = {
    SUSPENDED: 'suspended',
    CUSTOMER_CONFIRMATION: 'customer confirmation'
} as const;
const getPermissionStatus = (accountPermissions, permission: typeof PERMISSIONS[keyof typeof PERMISSIONS]) =>
    accountPermissions?.find(item => item.permission === permission)?.status;

/**
 * Navigation menu for message sending settings
 * Displays message sending options and communication channels
 */
function BrandingMenu(): JSX.Element {
    const {t} = useTranslation('branding');
    const navigate = useNavigate();
    const isMobile = useMobile();
    const subMenuShow = useShow();
    const {messageType, deliveryType} = useBrandingParams();
    const event = useSelector((s: RootState)=>({
        path: eventPathHelper[s.event.type] || eventPathHelper.default,
        type: s.event.type,
        settings: s.event.settings,
        eid: s.event.eid
    }));
    
    const accountPermissions = useSelector((s: RootState) => s.account?.permissions);
    const pid = useSelector((s: RootState) => s.account?.pid);
    // Check if WhatsApp message setting is active
    const disabledDeliveryTypes: ('email' | 'sms' | 'whatsapp')[] = [];
    
    const susStatus = event.settings?.suspended?.susStatus;
    const customerConfirmation = event.settings?.suspended?.customerConfirmation;
    const hasBillings = event.settings?.billings?.hasBillings;
    const [suspendedStatus, confirmationStatus] = useMemo(() => {
        return [
            getPermissionStatus(accountPermissions, PERMISSIONS.SUSPENDED),
            getPermissionStatus(accountPermissions, PERMISSIONS.CUSTOMER_CONFIRMATION)
        ];
    }, [accountPermissions]);

    const allowedMessageTypes = useMemo(()=>{
        return Object.keys(messageTypeConfig).filter((item) => messageTypeConfig[item].checkAccess({
            susStatus,
            confirmationStatus,
            customerConfirmation,
            suspendedStatus,
            hasBillings,
            pid
        }));
    },[ susStatus, confirmationStatus, customerConfirmation, suspendedStatus, hasBillings, pid]);

    useEffect(()=>{
        if(!deliveryTypeIcons[deliveryType] || !messageTypeConfig[messageType]){
            navigate(`/e/${event.eid}/${event.path}/branding/${MESSAGE_TYPES.sendTicket}/${DELIVERY_TYPES.whatsapp}`);
        } else if(!isMobile) subMenuShow.open();
    },[deliveryType, messageType]);

    const setMessageType = (newMessageType: string): void => {
        if(messageType !== newMessageType || !subMenuShow.isOpen)
            subMenuShow.open();
        else
            subMenuShow.close();

        const targetDeliveryType = resolveDeliveryType(
            newMessageType as typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES],
            deliveryType,
        );

        navigate(`/e/${event.eid}/${event.path}/branding/${newMessageType}/${targetDeliveryType}`);
    };

    const setDeliveryType = (newDeliveryType: keyof typeof deliveryTypeIcons): void => {
        if(!deliveryTypeIcons[newDeliveryType]) return;
        navigate(`/e/${event.eid}/${event.path}/branding/${messageType}/${newDeliveryType}`);
        if(isMobile) subMenuShow.close();
    };

    const allowedDeliveryTypes = getAllowedDeliveryTypes(messageType);

    return (
        <css.MenuContainer>
            <css.MenuList>
                {allowedMessageTypes.map((mType) => (
                    <MessageTypeItem
                        key={mType}
                        mType={mType}
                        isSelected={mType === messageType}
                        isOpen={subMenuShow.isOpen}
                        messageConfig={messageTypeConfig[mType]}
                        onSelect={setMessageType}
                        deliveryTypesLength={messageTypeConfig[mType]?.allowedDeliveryTypes?.length}
                    >
                        <DeliveryTypeMenu
                            isVisible={messageType === mType && subMenuShow.isOpen}
                            isMobile={isMobile}
                            deliveryTypeIcons={deliveryTypeIcons}
                            selectedDeliveryType={deliveryType}
                            onSelect={(dType) => setDeliveryType(dType as keyof typeof deliveryTypeIcons)}
                            allowedDeliveryTypes={messageTypeConfig[mType]?.allowedDeliveryTypes || ['email']}
                            disabledDeliveryTypes={disabledDeliveryTypes}
                        />
                    </MessageTypeItem>
                ))}
                {subMenuShow.isOpen && isMobile && (
                    <List
                        onClick={type => setDeliveryType(type.key)}
                        showClass={subMenuShow.showClass}
                        close={subMenuShow.close}
                        list={Object.keys(deliveryTypeIcons)
                            .filter(key => allowedDeliveryTypes.includes(key as 'email' | 'sms' | 'whatsapp'))
                            .map(key => ({key, text: t(key)}))
                        }
                    />
                )}
            </css.MenuList>
        </css.MenuContainer>
    );
}

export default BrandingMenu;
